import { useState, useEffect, useCallback } from 'react';
import { masterDropdownService, MasterOption } from '../services/masterDropdownService';

export type MasterType = 'states' | 'cities' | 'colors' | 'locations' | 'sets' | 'items';

interface UseMasterDropdownOptions {
  masterType: MasterType;
  stateId?: number | string; // For cities dropdown (deprecated - use stateName)
  stateName?: string; // For cities dropdown - state name
  enabled?: boolean; // To control when to fetch
}

export const useMasterDropdown = ({
  masterType,
  stateId,
  stateName,
  enabled = true,
}: UseMasterDropdownOptions) => {
  const [options, setOptions] = useState<MasterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOptions = useCallback(async () => {
    if (!enabled) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let data: MasterOption[] = [];

      switch (masterType) {
        case 'states':
          data = await masterDropdownService.getStatesDropdown();
          break;
        case 'cities':
          // Prefer stateName over stateId
          data = await masterDropdownService.getCitiesDropdown(stateName || stateId);
          break;
        case 'colors':
          data = await masterDropdownService.getColorsDropdown();
          break;
        case 'locations':
          data = await masterDropdownService.getLocationsDropdown();
          break;
        case 'sets':
          data = await masterDropdownService.getSetsDropdown();
          break;
        case 'items':
          data = await masterDropdownService.getItemsDropdown();
          break;
        default:
          data = [];
      }

      setOptions(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch dropdown options');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [masterType, stateId, stateName, enabled]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  return {
    options,
    loading,
    error,
    refetch: fetchOptions,
  };
};

